import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import {
    cancelAdminGiveaway,
    createAdminGiveaway,
    drawAdminGiveaway,
    fetchAdminGiveaway,
    fetchAdminGiveaways,
    transitionAdminGiveaway,
    updateAdminGiveaway,
    uploadAdminGiveawayImage,
} from '../../../services/Http/AdminApi';
import StandardModal from '../../../shared/layout/modals/StandardModal';

const Wrapper = styled.div`
  padding: 1.5rem;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  margin: 0;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 1.25rem;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: #374151;
  margin-bottom: 0.75rem;
`;

const Input = styled.input`
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.625rem 0.75rem;
`;

const TextArea = styled.textarea`
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.625rem 0.75rem;
  min-height: 70px;
  font-family: inherit;
`;

const Row = styled.div`
  display: flex;
  gap: 0.625rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  border: 1px solid ${({ $danger }) => ($danger ? '#dc2626' : '#d1d5db')};
  background: ${({ $primary, $danger }) => ($primary ? '#111827' : $danger ? '#fef2f2' : '#fff')};
  color: ${({ $primary, $danger }) => ($primary ? '#fff' : $danger ? '#dc2626' : '#111827')};
  border-radius: 8px;
  padding: 0.5625rem 0.875rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

const Item = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0.875rem;
  cursor: pointer;
  background: #fff;

  &:hover {
    border-color: #111827;
  }
`;

const Badge = styled.span`
  display: inline-block;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.1875rem 0.5rem;
  border-radius: 999px;
  background: ${({ $status }) =>
        ({
            draft: '#f3f4f6',
            active: '#dcfce7',
            closed: '#fef9c3',
            drawn: '#dbeafe',
            cancelled: '#fee2e2',
        }[$status] || '#f3f4f6')};
  color: #374151;
`;

const Muted = styled.div`
  color: #6b7280;
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const Preview = styled.img`
  max-width: 100%;
  max-height: 140px;
  border-radius: 8px;
  margin-top: 0.5rem;
`;

const SectionTitle = styled.div`
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
  margin: 1.375rem 0 0.625rem;
  padding-bottom: 0.375rem;
  border-bottom: 1px solid #f3f4f6;

  &:first-of-type {
    margin-top: 0;
  }
`;

const Hint = styled.span`
  font-size: 0.6875rem;
  color: #9ca3af;
  line-height: 1.45;
`;

const Stats = styled.div`
  display: flex;
  gap: 1.625rem;
  margin-top: 1.125rem;
  padding-top: 0.875rem;
  border-top: 1px solid #f3f4f6;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;

  b {
    font-size: 1.25rem;
    line-height: 1.1;
  }

  span {
    font-size: 0.6875rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

const WinnerBox = styled.div`
  margin-top: 0.875rem;
  padding: 0.75rem 0.875rem;
  border-radius: 8px;
  background: #dbeafe;
  font-size: 0.8125rem;
`;

/** What each status means, and what the buttons will do next. */
const STATUS_HELP = {
    draft: 'Not visible to anyone yet. Fill it in, then publish.',
    active: 'Live. People can spend points on tickets until the end date.',
    closed: 'Entries are closed. Review the entrants, then draw a winner.',
    drawn: 'Finished. The winner is recorded and this can no longer be changed.',
    cancelled: 'Cancelled. Every ticket was refunded to the points it was bought with.',
};

const EMPTY = {
    title: '',
    description: '',
    prize_description: '',
    image_url: '',
    ticket_cost_points: 50,
    max_tickets_per_user: '',
    starts_at: '',
    ends_at: '',
};

const toInput = (iso) => (iso ? iso.slice(0, 16) : '');

/** "ends in 12 days" reads faster than a date when scanning a list. */
const daysLeft = (iso) => {
    const days = Math.ceil((new Date(iso) - Date.now()) / 86400000);
    if (days < 0) return 'ended';
    if (days === 0) return 'ends today';
    if (days === 1) return 'ends tomorrow';
    return `ends in ${days} days`;
};

const AdminGiveaways = () => {
    const [list, setList] = useState([]);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [editorOpen, setEditorOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await fetchAdminGiveaways();
            setList(res.data || []);
        } catch (e) {
            toast.error('Could not load giveaways.');
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const open = async (id) => {
        try {
            const res = await fetchAdminGiveaway(id);
            setSelected(res.data);
            setForm({
                ...EMPTY,
                ...res.data,
                max_tickets_per_user: res.data.max_tickets_per_user ?? '',
                starts_at: toInput(res.data.starts_at),
                ends_at: toInput(res.data.ends_at),
            });
            setEditorOpen(true);
        } catch (e) {
            toast.error('Could not open that giveaway.');
        }
    };

    const startNew = () => {
        setSelected(null);
        setForm(EMPTY);
        setEditorOpen(true);
    };

    const closeEditor = () => {
        setEditorOpen(false);
        setSelected(null);
        setForm(EMPTY);
    };

    const payload = () => ({
        ...form,
        // Store the path, not the absolute URL: the image then survives a
        // change of CDN host, and the API resolves it on the way out.
        image_url: form.image_path || form.image_url || null,
        ticket_cost_points: Number(form.ticket_cost_points) || 0,
        max_tickets_per_user: form.max_tickets_per_user === '' ? null : Number(form.max_tickets_per_user),
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
    });

    const save = async () => {
        setLoading(true);
        try {
            if (selected) {
                await updateAdminGiveaway(selected.id, payload());
                toast.success('Saved.');
                await open(selected.id);
            } else {
                const res = await createAdminGiveaway(payload());
                toast.success('Created as a draft.');
                await open(res.data.id);
            }
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.message || 'Could not save.');
        } finally {
            setLoading(false);
        }
    };

    const move = async (status) => {
        setLoading(true);
        try {
            await transitionAdminGiveaway(selected.id, status);
            toast.success(status === 'active' ? 'Published.' : 'Entries closed.');
            await open(selected.id);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.message || 'That is not allowed.');
        } finally {
            setLoading(false);
        }
    };

    const draw = async () => {
        if (!window.confirm('Draw a winner? This cannot be undone, and the giveaway can no longer be cancelled.')) return;
        setLoading(true);
        try {
            const res = await drawAdminGiveaway(selected.id);
            toast.success(`Winner: ${res.winner?.email || res.winner?.user_id}`);
            await open(selected.id);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.message || 'Could not draw.');
        } finally {
            setLoading(false);
        }
    };

    const cancel = async () => {
        const tickets = selected?.tickets_count || 0;
        if (!window.confirm(`Cancel this giveaway? ${tickets} ticket(s) will be refunded in full, straight away.`)) return;
        setLoading(true);
        try {
            const res = await cancelAdminGiveaway(selected.id);
            toast.success(res.message);
            await open(selected.id);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.message || 'Could not cancel.');
        } finally {
            setLoading(false);
        }
    };

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    // Uploaded immediately rather than held until save: the admin sees at once
    // whether the picture is the right one, and a giveaway is never saved
    // pointing at a file that failed to arrive.
    const pickImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const res = await uploadAdminGiveawayImage(file);
            setForm((f) => ({ ...f, image_url: res.url, image_path: res.path }));
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Could not upload that image.');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };
    const status = selected?.status;
    const sold = (selected?.tickets_count || 0) > 0;

    return (
        <Wrapper>
            <Head>
                <Title>Giveaways</Title>
                <Button $primary onClick={startNew}>Create giveaway</Button>
            </Head>

            <List>
                {list.map((g) => (
                    <Item key={g.id} onClick={() => open(g.id)}>
                        <Row style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <strong>{g.title}</strong>
                                {g.prize_description ? <Muted style={{ marginTop: 2 }}>{g.prize_description}</Muted> : null}
                            </div>
                            <Badge $status={g.status}>{g.status}</Badge>
                        </Row>
                        <Muted>
                            {g.ticket_cost_points} points per ticket · {g.tickets_count} sold
                            {g.ends_at ? ` · ${daysLeft(g.ends_at)}` : ''}
                        </Muted>
                    </Item>
                ))}
                {list.length === 0 && (
                    <Card>
                        <strong>No giveaways yet</strong>
                        <Muted>
                            Press Create giveaway. Nothing is visible to anyone until you publish it.
                        </Muted>
                    </Card>
                )}
            </List>

            {/* The form lives in a modal so this page stays a list, and so the
                buttons sit in a footer that is pinned regardless of how long
                the form gets or how small the screen is. */}
            <StandardModal
                isOpen={editorOpen}
                onClose={closeEditor}
                width="720px"
                closeOnOverlayClick={false}
                title={
                    // Spans, not a div: this is rendered inside the modal's h3.
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <span>{selected ? selected.title || 'Giveaway' : 'New giveaway'}</span>
                        {selected && <Badge $status={status}>{status}</Badge>}
                    </span>
                }
                footer={
                    <>
                        {selected && ['draft', 'active', 'closed'].includes(status) && (
                            <Button $danger onClick={cancel} disabled={loading}>Cancel &amp; refund</Button>
                        )}
                        {status === 'draft' && <Button onClick={() => move('active')} disabled={loading}>Publish</Button>}
                        {status === 'active' && <Button onClick={() => move('closed')} disabled={loading}>Close entries</Button>}
                        {status === 'closed' && <Button onClick={draw} disabled={loading}>Draw winner</Button>}
                        <Button onClick={closeEditor} disabled={loading}>Close</Button>
                        <Button $primary onClick={save} disabled={loading || !form.title}>
                            {selected ? 'Save' : 'Create draft'}
                        </Button>
                    </>
                }
            >
                    {/* What the status means and what happens next, so the
                        buttons below do not have to be guessed at. */}
                    <Muted style={{ marginBottom: 6 }}>{STATUS_HELP[status] || STATUS_HELP.draft}</Muted>

                    <SectionTitle>What people see</SectionTitle>

                    <Field>
                        Name of the giveaway
                        <Input value={form.title} onChange={set('title')} placeholder="Summer giveaway" />
                        <Hint>The heading on the card. Not the prize — that is the next field.</Hint>
                    </Field>

                    <Field>
                        Prize
                        <Input value={form.prize_description || ''} onChange={set('prize_description')} placeholder="PlayStation 5 Pro 2TB" />
                        <Hint>Exactly what the winner gets, in one line. Shown in bold under the name.</Hint>
                    </Field>

                    <Field>
                        Description
                        <TextArea value={form.description || ''} onChange={set('description')} placeholder="Anything worth knowing: colour, model, when it ships, who can enter." />
                        <Hint>The paragraph under the prize on the public page. Optional, but it is the only place to put conditions.</Hint>
                    </Field>

                    <Field>
                        Image
                        <Input type="file" accept="image/jpeg,image/png,image/webp" onChange={pickImage} disabled={uploading} />
                        <Hint>JPG, PNG or WEBP, up to 5 MB. Landscape works best — the card crops to 200px tall.</Hint>
                        {uploading ? <Muted>Uploading…</Muted> : null}
                        {form.image_url ? (
                            <>
                                <Preview src={form.image_url} alt="" />
                                <Button type="button" onClick={() => setForm((f) => ({ ...f, image_url: '', image_path: '' }))}>
                                    Remove image
                                </Button>
                            </>
                        ) : null}
                    </Field>

                    <SectionTitle>Entry</SectionTitle>

                    <Field>
                        Points per ticket
                        <Input type="number" min="1" value={form.ticket_cost_points} onChange={set('ticket_cost_points')} disabled={sold} />
                        <Hint>
                            {sold
                                ? 'Tickets have been sold, so the price is locked — two people cannot pay differently for the same chance.'
                                : `A scanned receipt earns roughly 6 points today, so this is about ${Math.max(1, Math.round((Number(form.ticket_cost_points) || 0) / 6))} receipt(s) per ticket.`}
                        </Hint>
                    </Field>

                    <Field>
                        Max tickets per user
                        <Input type="number" min="1" placeholder="unlimited" value={form.max_tickets_per_user} onChange={set('max_tickets_per_user')} />
                        <Hint>Leave empty for unlimited. A cap stops one person with a large balance owning the draw.</Hint>
                    </Field>

                    <SectionTitle>When it runs</SectionTitle>

                    <Field>
                        Starts
                        <Input type="datetime-local" value={form.starts_at} onChange={set('starts_at')} />
                        <Hint>Leave empty to start as soon as you publish.</Hint>
                    </Field>

                    <Field>
                        Ends
                        <Input type="datetime-local" value={form.ends_at} onChange={set('ends_at')} />
                        <Hint>After this, entries stop but the winner is not drawn until you press Draw.</Hint>
                    </Field>

                    {selected && (
                        <Stats>
                            <Stat><b>{selected.tickets_count}</b><span>tickets</span></Stat>
                            <Stat><b>{selected.unique_entrants ?? 0}</b><span>entrants</span></Stat>
                            <Stat><b>{(selected.tickets_count || 0) * (selected.ticket_cost_points || 0)}</b><span>points spent</span></Stat>
                        </Stats>
                    )}

                    {selected?.winner && (
                        <WinnerBox>
                            Winner: <b>{selected.winner.name || selected.winner.email || selected.winner.user_id}</b>
                            {selected.winner.email ? <Muted>{selected.winner.email}</Muted> : null}
                        </WinnerBox>
                    )}

                    {selected?.events?.length ? (
                        <>
                            <SectionTitle>History</SectionTitle>
                            {selected.events.map((e) => (
                                <Muted key={e.id} style={{ marginTop: 0 }}>
                                    {new Date(e.created_at).toLocaleString()} — {e.action}
                                    {e.actor ? ` by ${e.actor.email}` : ''}
                                </Muted>
                            ))}
                        </>
                    ) : null}
            </StandardModal>
        </Wrapper>
    );
};

export default AdminGiveaways;
